/**
 * Queue — admin
 *
 * The connector's live background work, on every retailer account, in one place.
 *
 * WHY IT IS ITS OWN PAGE. This table was the fourth card down Retailer Accounts, under the
 * accounts list, the SKU map and the global orders table. "Is anything stuck?" is the
 * question this screen answers and it sat furthest from the top of the longest page in the
 * console — and it is not a question about one account, so it had no business living on a
 * page that is. Nothing here is scoped: no brandId is passed, which is what makes the table
 * show every job rather than one brand's.
 *
 * The queue is live and ages out. Sync History, beside it in the sidebar, is the permanent
 * record — the two are read together, which is why they moved together.
 */

import { useEffect, useState } from 'react';
import { Page, Layout, Card, Text, Box, BlockStack, InlineStack } from '@shopify/polaris';
import QueueTable from '../components/QueueTable';
import SharedInfoTip from '../components/InfoTip';
import apiClient from '../utils/api';

export default function BackgroundQueue() {
  const [retailers, setRetailers] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const data = await apiClient.getBrands({ limit: 500 });
        // The brands route answers with a list or with { brands: [...] } depending on its
        // parameters, and a wrong guess here empties the filter rather than throwing.
        const list = Array.isArray(data) ? data : (data?.brands || []);
        if (live) setBrands(list);
      } catch {
        // Silent for the same reason as the retailer list below: it is filter options, not
        // the rows.
      }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const data = await apiClient.getConnectorMarketplaces();
        if (live) setRetailers(data || []);
      } catch {
        // Deliberately silent. This list is the retailer FILTER's options and nothing else —
        // QueueTable fetches the jobs itself and reports its own failure in its own banner.
        // An error message here would sit above a table that had loaded perfectly well and
        // read as "the queue is broken", which is the opposite of what happened.
      }
    })();
    return () => { live = false; };
  }, []);

  // QueueTable's filter takes Polaris Select options; SyncHistoryTable takes the raw accounts
  // and builds its own. The two prop shapes differ, so neither page can pass the other's.
  const marketplaceOptions = retailers.map((m) => ({ label: m.name, value: m.id }));
  const brandOptions = brands.map((b) => ({ label: b.name, value: b.id }));

  return (
    <Page fullWidth>
      <Layout>
        <Layout.Section>
          <BlockStack gap="050">
            <InlineStack gap="100" blockAlign="center">
              <Text variant="headingLg" as="h1">Queue</Text>
              {/* The line that stood under this heading said "Background work for the
                  connector — collecting orders from retailers, and sending stock levels and
                  tracking back to them". It named one of the two systems this page shows and
                  described that one's jobs, so a billing run in the table read as something
                  that should not have been there. What the page covers belongs in one place,
                  and the tip is where the other caveat already lived. */}
              <SharedInfoTip content="Every background job 2W runs for its brands — the retailer connector and billing both. Completed jobs are kept for two hours, so an empty table on a quiet day is normal." />
            </InlineStack>
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              {/* No brandId: this is every job on every account, for every brand, from both
                  systems. The brand-scoped placement of the same component lives on a brand's
                  Retail Connect tab; here the brand is a filter instead. */}
              <QueueTable marketplaceOptions={marketplaceOptions} brandOptions={brandOptions} />
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
